import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Save, Gift } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OCCASIONS = ["Birthday", "Anniversary", "Baby Shower", "Housewarming", "Engagement", "Retirement", "Other"];
const PACKAGES = ["Basic", "Classic", "Premium", "Luxury", "Custom"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-[#475569] mb-1 block">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#E83E8C] outline-none text-sm";

/**
 * Edit customer & booking details from the CRM. Reuses the existing
 * PUT /api/admin/bookings/{id} endpoint (no new booking workflow).
 */
export default function BookingEditModal({ booking, authAxios, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: booking.date || "",
    setup_time: booking.setup_time || booking.event_time || booking.time_slot || "",
    customer_name: booking.customer_name || "",
    customer_phone: booking.customer_phone || "",
    customer_email: booking.customer_email || "",
    package: booking.package || "",
    occasion: booking.occasion || "",
    pincode: booking.pincode || "",
    address: booking.address || "",
    notes: booking.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.date) { toast.error("Event date is required"); return; }
    setSaving(true);
    try {
      // Preserve payment/status fields — the update endpoint only overwrites
      // what we send; carry the originals so nothing is lost.
      await authAxios.put(`${API_URL}/api/admin/booking-edit/${booking.id}`, {
        ...form,
        payment_type: booking.payment_type,
        payment_amount: booking.payment_amount,
        payment_status: booking.payment_status,
        sales_executive: booking.sales_executive,
        booking_status: booking.booking_status,
      });
      toast.success("Booking updated");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the booking");
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="booking-edit-modal">
      <div className="absolute inset-0 bg-[#1E1B4B]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3.5 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-[#1E1B4B]">Edit Booking</h3>
            <p className="text-[11px] text-[#94a3b8] font-mono">{booking.id}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-[#94a3b8] hover:bg-gray-100" data-testid="booking-edit-close"><X size={18} /></button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Customer Name"><input className={inputCls} value={form.customer_name} onChange={set("customer_name")} data-testid="edit-customer-name" /></Field>
          <Field label="Phone"><input className={inputCls} value={form.customer_phone} onChange={set("customer_phone")} data-testid="edit-customer-phone" /></Field>
          <Field label="Email"><input className={inputCls} value={form.customer_email} onChange={set("customer_email")} data-testid="edit-customer-email" /></Field>
          <Field label="Event Date"><input type="date" className={inputCls} value={form.date} onChange={set("date")} data-testid="edit-date" /></Field>
          <Field label="Setup Time"><input className={inputCls} value={form.setup_time} onChange={set("setup_time")} placeholder="e.g. 6:00 PM" data-testid="edit-setup-time" /></Field>
          <Field label="Occasion">
            <select className={inputCls} value={OCCASIONS.includes(form.occasion) ? form.occasion : "Other"} onChange={set("occasion")} data-testid="edit-occasion">
              {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Package">
            <select className={inputCls} value={form.package} onChange={set("package")} data-testid="edit-package">
              <option value="">—</option>
              {PACKAGES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Pincode"><input className={inputCls} value={form.pincode} onChange={set("pincode")} data-testid="edit-pincode" /></Field>
          <div className="col-span-2">
            <Field label="Full Address"><textarea className={inputCls} rows={2} value={form.address} onChange={set("address")} data-testid="edit-address" /></Field>
          </div>

          {/* Read-only: add-ons the customer selected (name + price) */}
          {(booking.add_ons || []).length > 0 && (
            <div className="col-span-2" data-testid="edit-addons-section">
              <span className="text-xs font-semibold text-[#475569] mb-1 flex items-center gap-1"><Gift size={12} className="text-[#FF9F1C]" /> Selected Add-ons</span>
              <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
                {(booking.add_ons || []).map((a, i) => {
                  const name = typeof a === "string" ? a : (a.name || a.id || "Add-on");
                  const qty = (a && a.quantity) || 1;
                  const line = typeof a === "object" ? Number(a.subtotal ?? (a.price || 0) * qty) : 0;
                  return (
                    <div key={`${name}-${i}`} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`edit-addon-row-${i}`}>
                      <span className="text-[#1E1B4B]">{name}{qty > 1 ? ` × ${qty}` : ""}{a.custom_note ? ` — ${a.custom_note}` : ""}</span>
                      <span className="font-semibold text-[#1E1B4B]">₹{Number(line || 0).toLocaleString("en-IN")}</span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-3 py-1.5 text-xs bg-[#FFFAF5]">
                  <span className="font-semibold text-[#475569]">Add-ons Total</span>
                  <span className="font-bold text-[#E83E8C]" data-testid="edit-addons-total">₹{Number(booking.add_ons_total ?? (booking.add_ons || []).reduce((s, a) => s + Number((typeof a === "object" ? (a.subtotal ?? (a.price || 0) * (a.quantity || 1)) : 0)), 0)).toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>
          )}

          <div className="col-span-2">
            <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={set("notes")} data-testid="edit-notes" /></Field>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-[#475569] hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-[#E83E8C] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#d1357e]" data-testid="booking-edit-save">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
